import React, { useState } from "react";
import styled from "@emotion/styled";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, FolderOpen, Trash2, Edit2, Calendar, FileText, Search, Download, Upload } from "lucide-react";
import toast from "react-hot-toast";
import { useDiagramStore } from "../store/diagramStore";
import generateDiagramPrompt from "../prompts/generateDiagramPrompt";

const ManagerContainer = styled.div`
  padding: 40px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #666;
`;

const ActionBar = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
  flex-wrap: wrap;
  align-items: center;
`;

const SearchBar = styled.div`
  flex: 1;
  min-width: 300px;
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
`;

const Button = styled(motion.button)<{ $variant?: "primary" | "secondary" }>`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  ${(props) =>
    props.$variant === "secondary"
      ? `
    background: white;
    color: #333;
    border: 1px solid #ddd;
    &:hover {
      background: #f5f5f5;
      border-color: #667eea;
    }
  `
      : `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    &:hover { opacity: 0.9; }
  `}
`;

const DiagramGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
`;

const DiagramCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;

  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    border-color: #667eea;
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 12px;
`;

const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const CardActions = styled.div`
  display: flex;
  gap: 4px;
`;

const IconButton = styled(motion.button)`
  padding: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #333;
  }
`;

const CardDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 16px;
  line-height: 1.5;
`;

const CardStats = styled.div`
  display: flex;
  gap: 16px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
`;

const Stat = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #999;
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 32px;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 24px;
  color: #333;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #555;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 24px;
`;

interface DiagramManagerProps {
  onOpenDiagram: (id: string) => void;
}

export const DiagramManager: React.FC<DiagramManagerProps> = ({ onOpenDiagram }) => {
  const { diagrams, createNewDiagram, deleteDiagram, importDiagram, exportDiagram, loadDiagram } = useDiagramStore();
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [newDiagramData, setNewDiagramData] = useState({
    name: "",
    description: "",
  });

  const filteredDiagrams = diagrams.filter((diagram) => diagram.name.toLowerCase().includes(searchTerm.toLowerCase()) || diagram.description?.toLowerCase().includes(searchTerm.toLowerCase()));

  const handleCreateDiagram = () => {
    if (!newDiagramData.name) {
      toast.error("Diagram name is required");
      return;
    }

    createNewDiagram(newDiagramData.name, newDiagramData.description);
    setShowCreateModal(false);
    setNewDiagramData({ name: "", description: "" });
    toast.success("Diagram created successfully");

    // Open the newly created diagram
    const newDiagram = diagrams[diagrams.length - 1];
    if (newDiagram) {
      onOpenDiagram(newDiagram.id);
    }
  };

  const handleDeleteDiagram = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this diagram?")) {
      await deleteDiagram(id);
      toast.success("Diagram deleted");
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleImport = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = async (event) => {
          try {
            const jsonString = event.target?.result as string;
            await importDiagram(jsonString);
            toast.success("Diagram imported successfully");

            // Get the newly imported diagram (it should be the last one)
            const updatedDiagrams = useDiagramStore.getState().diagrams;
            const importedDiagram = updatedDiagrams[updatedDiagrams.length - 1];
            if (importedDiagram) {
              // Automatically open the imported diagram
              onOpenDiagram(importedDiagram.id);
            }
          } catch (error) {
            toast.error("Failed to import diagram");
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const handleExportDiagram = async (diagramId: string, diagramName: string) => {
    try {
      // Load the diagram first
      await loadDiagram(diagramId);

      // Export it
      const json = exportDiagram();
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${diagramName || "diagram"}.json`;
      a.click();
      URL.revokeObjectURL(url);

      setShowExportModal(false);
      toast.success("Diagram exported");
    } catch (error) {
      toast.error("Failed to export diagram");
    }
  };

  return (
    <ManagerContainer>
      <Header>
        <Title>Service Diagram Editor</Title>
        <Subtitle>Create and manage interactive service architecture diagrams</Subtitle>
      </Header>

      <ActionBar>
        <SearchBar>
          <SearchIcon size={20} />
          <SearchInput placeholder="Search diagrams..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </SearchBar>

        <Button onClick={() => setShowCreateModal(true)} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Plus size={20} />
          New Diagram
        </Button>

        <Button $variant="secondary" onClick={handleImport} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Upload size={20} />
          Import
        </Button>

        <Button $variant="secondary" onClick={() => setShowExportModal(true)} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} disabled={diagrams.length === 0}>
          <Download size={20} />
          Export
        </Button>

        <Button
          $variant="secondary"
          onClick={async () => {
            await navigator.clipboard.writeText(generateDiagramPrompt);
            toast.success("LLM prompt copied to clipboard");
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Copy LLM Prompt
        </Button>
      </ActionBar>

      {filteredDiagrams.length === 0 ? (
        <EmptyState>
          <EmptyIcon>
            <FileText size={40} color="white" />
          </EmptyIcon>
          <EmptyTitle>No diagrams yet</EmptyTitle>
          <EmptyText>Create your first service architecture diagram to get started</EmptyText>
          <Button onClick={() => setShowCreateModal(true)} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Plus size={20} />
            Create First Diagram
          </Button>
        </EmptyState>
      ) : (
        <DiagramGrid>
          {filteredDiagrams.map((diagram) => (
            <DiagramCard key={diagram.id} onClick={() => onOpenDiagram(diagram.id)} whileHover={{ y: -4 }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
              <CardHeader>
                <CardTitle>{diagram.name}</CardTitle>
                <CardActions>
                  <IconButton onClick={(e) => handleDeleteDiagram(diagram.id, e)} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                    <Trash2 size={18} />
                  </IconButton>
                </CardActions>
              </CardHeader>

              {diagram.description && <CardDescription>{diagram.description}</CardDescription>}

              <CardStats>
                <Stat>
                  <FolderOpen size={14} />
                  {diagram.nodeCount || 0} services
                </Stat>
                <Stat>
                  <Edit2 size={14} />
                  {diagram.connectionCount || 0} connections
                </Stat>
                <Stat>
                  <Calendar size={14} />
                  {formatDate(diagram.updatedAt)}
                </Stat>
              </CardStats>
            </DiagramCard>
          ))}
        </DiagramGrid>
      )}

      <AnimatePresence>
        {showCreateModal && (
          <Modal initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowCreateModal(false)}>
            <ModalContent initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} onClick={(e) => e.stopPropagation()}>
              <ModalTitle>Create New Diagram</ModalTitle>

              <FormGroup>
                <Label>Diagram Name *</Label>
                <Input
                  value={newDiagramData.name}
                  onChange={(e) =>
                    setNewDiagramData((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  placeholder="e.g., FreshApp Architecture"
                  autoFocus
                />
              </FormGroup>

              <FormGroup>
                <Label>Description</Label>
                <TextArea
                  value={newDiagramData.description}
                  onChange={(e) =>
                    setNewDiagramData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Describe the purpose of this diagram..."
                />
              </FormGroup>

              <ModalActions>
                <Button $variant="secondary" onClick={() => setShowCreateModal(false)} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  Cancel
                </Button>
                <Button onClick={handleCreateDiagram} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  Create Diagram
                </Button>
              </ModalActions>
            </ModalContent>
          </Modal>
        )}

        {showExportModal && (
          <Modal initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowExportModal(false)}>
            <ModalContent initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} onClick={(e) => e.stopPropagation()}>
              <ModalTitle>Export Diagram</ModalTitle>
              <p style={{ fontSize: "14px", color: "#666", marginBottom: "20px" }}>Select a diagram to export as JSON file</p>

              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                {diagrams.map((diagram) => (
                  <DiagramCard key={diagram.id} onClick={() => handleExportDiagram(diagram.id, diagram.name)} whileHover={{ scale: 1.02 }} style={{ marginBottom: "12px" }}>
                    <CardHeader>
                      <CardTitle>{diagram.name}</CardTitle>
                    </CardHeader>
                    {diagram.description && <CardDescription>{diagram.description}</CardDescription>}
                    <CardStats>
                      <Stat>
                        <FolderOpen size={14} />
                        {diagram.nodeCount || 0} services
                      </Stat>
                      <Stat>
                        <Edit2 size={14} />
                        {diagram.connectionCount || 0} connections
                      </Stat>
                    </CardStats>
                  </DiagramCard>
                ))}
              </div>

              <ModalActions>
                <Button $variant="secondary" onClick={() => setShowExportModal(false)} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  Cancel
                </Button>
              </ModalActions>
            </ModalContent>
          </Modal>
        )}
      </AnimatePresence>
    </ManagerContainer>
  );
};
